/**
 * Shared label/style helper functions.
 * Previously duplicated across CandidateProfile, CandidateList, ActionReports,
 * AdminActionReports, ActionReportsList, and TranscriptManager.
 *
 * Status/stage functions accept an optional `statusConfig` object (from
 * systemConfig/statusOptions in Firestore). When provided, dynamic labels from
 * the database take precedence; otherwise fallback labels are returned.
 */

// ── Conference status ────────────────────────────────────────────────────────

const CONFERENCE_STATUS_LABELS = {
  'lay': 'Lay',
  'clm': 'Certified Lay Ministry',
  'cmc': 'Certified Ministry Candidate',
  'supply-pastor': 'Supply Pastor',
  'local-pastor': 'Local Pastor',
  'other-fellowship': 'Other Fellowship',
  'orders-of-faith': 'Orders of Faith',
  'other': 'Other',
  // Legacy aliases
  'local_pastor': 'Local Pastor',
  'supply_pastor': 'Supply Pastor',
};

export function getConferenceStatusLabel(status, statusConfig = null) {
  if (statusConfig && statusConfig.conferenceStatuses) {
    const found = statusConfig.conferenceStatuses.find(s => s.id === status);
    if (found) return found.label;
  }
  return CONFERENCE_STATUS_LABELS[status] || status || '';
}

export function getConferenceStatusStyle(status, statusConfig = null) {
  if (statusConfig && statusConfig.conferenceStatuses) {
    const found = statusConfig.conferenceStatuses.find(s => s.id === status);
    if (found && found.color) {
      return { backgroundColor: found.color, color: 'white' };
    }
  }
  return {};
}

// ── Process stage ────────────────────────────────────────────────────────────

const PROCESS_STAGE_LABELS = {
  'inquiring': 'Inquiring',
  'certified': 'Certified',
  'full-time': 'Full-Time',
  'part-time': 'Part-Time',
  'completed': 'Completed',
  'discontinued': 'Discontinued',
  'archived': 'Archived',
  // Legacy aliases
  'inquiry': 'Inquiring',
  'candidacy': 'Certified',
  'provisional': 'Part-Time',
  'full-connection': 'Full-Time',
};

export function getProcessStageLabel(stage, statusConfig = null) {
  if (statusConfig && statusConfig.processStages) {
    const found = statusConfig.processStages.find(s => s.id === stage);
    if (found) return found.label;
  }
  return PROCESS_STAGE_LABELS[stage] || stage || '';
}

export function getProcessStageStyle(stage, statusConfig = null) {
  if (statusConfig && statusConfig.processStages) {
    const found = statusConfig.processStages.find(s => s.id === stage);
    if (found && found.color) {
      return { backgroundColor: found.color, color: 'white' };
    }
  }
  return {};
}

// ── Action report type ────────────────────────────────────────────────────────

const REPORT_TYPE_LABELS = {
  'dcom-review': 'DCOM Review',
  'bom-review': 'BOM Review',
  'candidacy-interview': 'Candidacy Interview',
  'provisional-review': 'Provisional Review',
  'ordination-review': 'Ordination Review',
  'annual-review': 'Annual Review',
  'other': 'Other',
};

export function getReportTypeLabel(type) {
  return REPORT_TYPE_LABELS[type] || type || '';
}

// ── Action report status ──────────────────────────────────────────────────────

const REPORT_STATUS_COLORS = {
  'pending': 'status-pending',
  'approved': 'status-approved',
  'denied': 'status-denied',
  'deferred': 'status-deferred',
  'completed': 'status-completed',
};

export function getStatusColor(status) {
  return REPORT_STATUS_COLORS[status] || '';
}

// ── Vote totals ───────────────────────────────────────────────────────────────

export function getTotalVotes(vote) {
  if (!vote) return 0;
  return (vote.yes || 0) + (vote.no || 0) + (vote.abstain || 0);
}

// ── Date formatting ───────────────────────────────────────────────────────────

/**
 * Formats a date value to a readable string.
 * Accepts: Firestore Timestamp, JS Date, ISO string, timestamp number, or null.
 */
export function formatDate(value) {
  if (!value) return 'Not specified';

  let date;
  // Firestore Timestamp
  if (value && typeof value.toDate === 'function') {
    date = value.toDate();
  } else {
    date = new Date(value);
  }

  if (isNaN(date.getTime())) return 'Not specified';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
